import { useState, useEffect } from 'react';
import { fetchClients, fetchCases } from '../../api';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Building2,
  Users,
  IndianRupee,
  TrendingUp,
  Plus,
  Eye,
} from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-lg p-3 text-sm">
        <p className="text-slate-500 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: ₹{entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const emptyCommissionData = [
  { month: 'Aug', commission: 0 },
  { month: 'Sep', commission: 0 },
  { month: 'Oct', commission: 0 },
  { month: 'Nov', commission: 0 },
  { month: 'Dec', commission: 0 },
  { month: 'Jan', commission: 0 },
];

export default function BusinessPartner() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const clRes = await fetchClients();
        if (isMounted && Array.isArray(clRes)) {
          setClients(clRes);
        }
      } catch (e) {
        console.error('Failed to load clients in BP:', e);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  const totalCases = clients.reduce((sum, c) => sum + (c.totalCases || 0), 0);
  const revenueGen = totalCases * 250;
  const commEarned = Math.round(revenueGen * 0.05);

  const stats = [
    { label: 'Clients Added', value: clients.length, icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Total Cases', value: totalCases.toLocaleString(), icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Revenue Generated', value: `₹${(revenueGen / 100000).toFixed(1)}L`, icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Commission Earned', value: `₹${commEarned.toLocaleString()}`, icon: IndianRupee, color: 'text-green-600', bg: 'bg-emerald-50' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="business-partner-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Business Partner Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your clients and track commissions</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="add-client-btn">
          <Plus className="w-4 h-4" />
          Add Client
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="crm-card p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Commission Chart */}
        <div className="lg:col-span-2 crm-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-900">Commission Earnings</h3>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200 font-semibold text-xs">
              5% Commission Rate
            </Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={commissionData}>
                <defs>
                  <linearGradient id="colorCommission" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `₹${(v/1000)}K`} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="commission"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorCommission)"
                  name="Commission"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payout Summary */}
        <div className="crm-card p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Payout Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-sm text-slate-500">Total Earned</span>
              <span className="text-sm font-semibold text-emerald-600">₹2,00,000</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-sm text-slate-500">Paid Out</span>
              <span className="text-sm font-semibold text-slate-900">₹1,75,000</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-200">
              <span className="text-sm text-slate-500">Pending</span>
              <span className="text-sm font-semibold text-amber-600">₹25,000</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-semibold text-slate-900">Next Payout</span>
              <span className="text-sm text-slate-500">Jan 31, 2024</span>
            </div>
          </div>
          <Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 gap-2" data-testid="request-payout-btn">
            <IndianRupee className="w-4 h-4" />
            Request Payout
          </Button>
        </div>
      </div>

      {/* Clients List */}
      <div className="crm-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">Your Clients</h3>
          <span className="text-sm text-slate-500">{clients.length} clients</span>
        </div>
        <ScrollArea className="h-80">
          <div className="space-y-4">
            {clients.map((client) => (
              <div
                key={client.id}
                className="flex items-center justify-between p-4 rounded-lg bg-white hover:bg-slate-100/50 cursor-pointer transition-colors"
                onClick={() => setSelectedClient(client)}
                data-testid={`client-${client.id}`}
              >
                <div className="flex items-center gap-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={client.logo} />
                    <AvatarFallback>{client.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">{client.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-slate-500">{client.totalCases} cases</span>
                      <Badge 
                        variant="outline" 
                        className={client.status === 'active' 
                          ? 'bg-emerald-50 text-emerald-600 border-emerald-200 font-semibold text-xs' 
                          : 'bg-slate-100 text-slate-600 border-slate-200 font-semibold text-xs'
                        }
                      >
                        {client.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-emerald-600">
                    ₹{((client.totalCases * 250) * 0.05).toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">commission earned</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
